<template>
    <div>
        <ProductIdentitySection
            v-if="hasSection('identity')"
            :name="productName"
            :identifier="productIdentifier"
            :description="productDescription"
            :name-label="productNameLabel"
            :identifier-label="productIdentifierLabel"
            :description-label="productDescriptionLabel"
            :customer-product="context.request.customerProduct || {}"
            :show-description="showProductDescription"
            :show-customer-product-name="settings.showCustomerProductName"
            :show-customer-product-identifier="settings.showCustomerProductIdentifier"
            :show-customer-product-description="settings.showCustomerProductDescription"
            :show-customer-product-reference-only-when-different="settings.showCustomerProductReferenceOnlyWhenDifferent"
            @update:name="updateProductName"
            @update:identifier="updateProductIdentifier"
            @update:description="updateProductDescription"
        />

        <ProductWeightSection
            v-if="hasSection('product-weight') && context.productWeightRequired"
            :value="productWeight"
            @change="changeProductWeight"
        />

        <ProductSupplySection
            v-if="hasSection('purchase-source')"
            :type="product.type"
            :own-type="productTypes.OWN"
            :purchased-type="purchasedRadioType"
            :is-purchased="isPurchased"
            :suppliers="product.suppliers || []"
            @change-type="changeProductType"
            @change-suppliers="changeSuppliers"
        />

        <SupplierDetailsSection
            v-if="hasSection('supplier-details') && product.type === productTypes.PURCHASED && hasSupplier"
            :name="supplierName"
            :identifier="supplierIdentifier"
            :scip-number="supplierScipNumber"
            :name-label="supplierNameLabel"
            :identifier-label="supplierIdentifierLabel"
            :scip-number-label="supplierScipNumberLabel"
            @update:name="updateSupplierName"
            @update:identifier="updateSupplierIdentifier"
            @update:scip-number="updateSupplierScipNumber"
        />

        <ProductComplexitySection
            v-if="hasSection('complexity') && product.type === productTypes.OWN"
            :value="context.request.isComplexProduct"
            :included-substance-count="(context.includedSubstances || []).length"
            :can-switch-to-simple="canSwitchToSimple"
            @change="changeComplexity"
        />

        <MaterialMixtureCategoriesSection
            v-if="hasSection('material-mixture-categories')"
            :product="product"
            @change="changeMaterialMixtureCategories"
        />
    </div>
</template>

<script>
import {
    getNameOrId,
    ProductIdentifiers,
    ProductNames,
    ProductType,
    setNameOrId,
} from '@/libs/product'
import { createGeneralProductInformationV2Validation } from './general-product-information-v2-validation'
import MaterialMixtureCategoriesSection from './sections/MaterialMixtureCategoriesSection.vue'
import ProductComplexitySection from './sections/ProductComplexitySection.vue'
import ProductIdentitySection from './sections/ProductIdentitySection.vue'
import ProductSupplySection from './sections/ProductSupplySection.vue'
import ProductWeightSection from './sections/ProductWeightSection.vue'
import SupplierDetailsSection from './sections/SupplierDetailsSection.vue'

export default {
    name: 'GeneralProductInformationStepV2',
    components: {
        MaterialMixtureCategoriesSection,
        ProductComplexitySection,
        ProductIdentitySection,
        ProductSupplySection,
        ProductWeightSection,
        SupplierDetailsSection,
    },
    props: {
        context: { type: Object, required: true },
        settings: { type: Object, required: true },
        runtimeState: { type: Object, required: true },
    },
    computed: {
        product() {
            return this.context.product
        },
        productTypes() {
            return ProductType
        },
        productName() {
            return getNameOrId(this.product.names, ProductNames.OUR_PRODUCT_NAME) || ''
        },
        productIdentifier() {
            return getNameOrId(this.product.identifiers, ProductIdentifiers.PRODUCT_ID) || ''
        },
        productDescription() {
            return getNameOrId(this.product.names, ProductNames.PRODUCT_DESCRIPTION) || ''
        },
        showProductDescription() {
            const hasProductDescription = (this.product.names || [])
                .some(item => item.key === ProductNames.PRODUCT_DESCRIPTION)
            const customerDescription = this.context.request.customerProduct
                && this.context.request.customerProduct.description

            return hasProductDescription || String(customerDescription || '').trim().length > 0
        },
        supplierName() {
            return getNameOrId(this.product.names, ProductNames.SUPPLIERS_PRODUCT_NAME) || ''
        },
        supplierIdentifier() {
            return getNameOrId(this.product.identifiers, ProductIdentifiers.SUPPLIERS_PRODUCT_ID) || ''
        },
        supplierScipNumber() {
            return getNameOrId(this.product.identifiers, ProductIdentifiers.SUPPLIERS_SCIP_NUMBER) || ''
        },
        productNameLabel() {
            return this.getAttributeLabel('ProductName', ProductNames.OUR_PRODUCT_NAME)
        },
        productIdentifierLabel() {
            return this.getAttributeLabel('ProductId', ProductIdentifiers.PRODUCT_ID)
        },
        productDescriptionLabel() {
            return this.getAttributeLabel('ProductName', ProductNames.PRODUCT_DESCRIPTION)
        },
        supplierNameLabel() {
            return this.getAttributeLabel('ProductName', ProductNames.SUPPLIERS_PRODUCT_NAME)
        },
        supplierIdentifierLabel() {
            return this.getAttributeLabel('ProductId', ProductIdentifiers.SUPPLIERS_PRODUCT_ID)
        },
        supplierScipNumberLabel() {
            return this.getAttributeLabel('ProductId', ProductIdentifiers.SUPPLIERS_SCIP_NUMBER)
        },
        productWeight() {
            return this.product.characteristics && this.product.characteristics.weight
        },
        hasSupplier() {
            return Array.isArray(this.product.suppliers) && this.product.suppliers.length > 0
        },
        isPurchased() {
            return [ProductType.PURCHASED, ProductType.PURCHASED_UNDEFINED_SUPPLIER]
                .includes(this.product.type)
        },
        purchasedRadioType() {
            return this.hasSupplier ? ProductType.PURCHASED : ProductType.PURCHASED_UNDEFINED_SUPPLIER
        },
        canSwitchToSimple() {
            const hierarchy = this.context.productHierarchy || { children: [] }

            return !Array.isArray(hierarchy.children) || hierarchy.children.length === 0
        },
    },
    mounted() {
        this.emitValidity()
    },
    methods: {
        getAttributeLabel(type, key) {
            const attributes = this.context.productAttributes
            const attribute = attributes && Array.isArray(attributes[type])
                ? attributes[type].find(item => item.key === key)
                : null

            return attribute ? attribute.title : 'Loading...'
        },
        hasSection(section) {
            return this.settings.sections.includes(section)
        },
        setNameOrIdentifier(collection, key, value) {
            setNameOrId(collection, key, value)
            this.$emit('save-requested')
            this.emitValidity()
        },
        updateProductName(value) {
            this.setNameOrIdentifier(this.product.names, ProductNames.OUR_PRODUCT_NAME, value)
        },
        updateProductIdentifier(value) {
            this.setNameOrIdentifier(this.product.identifiers, ProductIdentifiers.PRODUCT_ID, value)
        },
        updateProductDescription(value) {
            this.setNameOrIdentifier(this.product.names, ProductNames.PRODUCT_DESCRIPTION, value)
        },
        updateSupplierName(value) {
            this.setNameOrIdentifier(this.product.names, ProductNames.SUPPLIERS_PRODUCT_NAME, value)
        },
        updateSupplierIdentifier(value) {
            this.setNameOrIdentifier(this.product.identifiers, ProductIdentifiers.SUPPLIERS_PRODUCT_ID, value)
        },
        updateSupplierScipNumber(value) {
            this.setNameOrIdentifier(this.product.identifiers, ProductIdentifiers.SUPPLIERS_SCIP_NUMBER, value)
        },
        changeProductType(type) {
            this.$emit('action', { type: 'product-type.change', payload: { type } })
        },
        changeSuppliers(ids, suppliers) {
            this.$emit('action', { type: 'suppliers.change', payload: { ids, suppliers } })
        },
        changeComplexity(isComplex) {
            this.$emit('action', { type: 'complexity.change', payload: { isComplex } })
        },
        changeProductWeight(value) {
            this.$emit('action', {
                type: 'characteristics.change',
                payload: { characteristics: { weight: Number.parseFloat(value) } },
            })
        },
        changeMaterialMixtureCategories({ materialCategoryIds, mixtureCategoryIds }) {
            this.$set(this.product, 'materialCategoryIds', materialCategoryIds)
            this.$set(this.product, 'mixtureCategoryIds', mixtureCategoryIds)
            this.$emit('save-requested')
            this.emitValidity()
        },
        emitValidity() {
            this.$emit(
                'validity-change',
                createGeneralProductInformationV2Validation(this.context, this.settings),
            )
        },
    },
    watch: {
        productName: 'emitValidity',
        supplierName: 'emitValidity',
        'product.type': 'emitValidity',
        'product.suppliers': 'emitValidity',
        'context.request.isComplexProduct': 'emitValidity',
        'runtimeState.isLoading': function onLoadingChange(isLoading) {
            if (!isLoading) {
                this.$nextTick(this.emitValidity)
            }
        },
    },
}
</script>
